import { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DataTable from "examples/Tables/DataTable";
import { Card } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUserHistory } from "services/usersServices";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import "./ReviewHistory.css";
import SearchBar from "../Components/SearchBar";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import AvatarWithLoader from "../Components/Avatar/AvatarWithLoader";

function UserHistory() {
  const [userHistory, setUserHistory] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);
  const columns = [
    {
      Header: "User",
      accessor: "user",
      Cell: ({ row }) => (
        <ArgonBox display="flex" alignItems="center">
          <AvatarWithLoader src={row.original.avatar} alt={row.original.name} />
          <ArgonTypography className="flagged-content-table-text">
            {row.original.name}
          </ArgonTypography>
        </ArgonBox>
      ),
    },
    {
      Header: "Reports Made",
      accessor: "reports_made_count",
      Cell: ({ row }) => {
        const count = row.original.reports_made_count;
        return count > 0 ? (
          <span
            className="user-history-modals-button"
            onClick={() => {
              navigate(
                `/admin/user-history/reports-made/${row.original.user_id}`,
                {
                  state: { user: row.original },
                }
              );
            }}
          >
            <ArgonTypography variant="h6">{count}</ArgonTypography>
            <ArgonTypography
              className="flagged-content-table-text view-details-text"
              fontWeight="medium"
            >
              View details
            </ArgonTypography>
          </span>
        ) : (
          <ArgonTypography variant="h6">-</ArgonTypography>
        );
      },
    },
    {
      Header: "Reports Against",
      accessor: "reports_against_count",
      Cell: ({ row }) => {
        const count = row.original.reports_against_count;
        return count > 0 ? (
          <span
            className="user-history-modals-button"
            onClick={() => {
              navigate(
                `/admin/user-history/reports-against/${row.original.user_id}`,
                {
                  state: { user: row.original },
                }
              );
            }}
          >
            <ArgonTypography variant="h6">{count}</ArgonTypography>
            <ArgonTypography
              className="flagged-content-table-text view-details-text"
              fontWeight="medium"
            >
              View details
            </ArgonTypography>
          </span>
        ) : (
          <ArgonTypography variant="h6">-</ArgonTypography>
        );
      },
    },
    {
      Header: "Activities Joined",
      accessor: "joined_activities_count",
      Cell: ({ row }) => {
        const count = row.original.joined_activities_count;
        return count > 0 ? (
          <span className="user-history-modals-button">
            <ArgonTypography variant="h6">{count}</ArgonTypography>
            <ArgonTypography
              className="flagged-content-table-text view-details-text"
              fontWeight="medium"
              onClick={() => {
                navigate(
                  `/admin/user-history/activity-joined/${row.original.user_id}`
                );
              }}
            >
              View details
            </ArgonTypography>
          </span>
        ) : (
          <ArgonTypography variant="h6">-</ArgonTypography>
        );
      },
    },
    {
      Header: "Activities Created",
      accessor: "created_activities_count",
      Cell: ({ row }) => {
        const count = row.original.created_activities_count;
        return count > 0 ? (
          <span className="user-history-modals-button">
            <ArgonTypography variant="h6">{count}</ArgonTypography>
            <ArgonTypography
              className="flagged-content-table-text view-details-text"
              fontWeight="medium"
              onClick={() => {
                navigate(
                  `/admin/user-history/activity-created/${row.original.user_id}`
                );
              }}
            >
              View details
            </ArgonTypography>
          </span>
        ) : (
          <ArgonTypography variant="h6">-</ArgonTypography>
        );
      },
    },
  ];

  useEffect(() => {
    try {
      setLoading(true);
      getUserHistory(debouncedSearchTerm || null).then((data) => {
        setUserHistory(data.data);
        setLoading(false);
      });
    } catch (error) {
      toast.error("Error fetching user history");
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search Users"
      />
      <Card
        sx={{
          overflow: "visible",
          marginTop: 6,
        }}
      >
        <ArgonBox padding={3}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <ArgonTypography variant="h5" fontWeight="medium">
              User History
            </ArgonTypography>
          </ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress size={40} />
            </ArgonBox>
          ) : userHistory.length > 0 ? (
            <DataTable
              table={{ columns, rows: userHistory }}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              pagination={false}
            />
          ) : (
            <ArgonBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <ArgonTypography variant="h6">
                No user history found
              </ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </Card>
    </>
  );
}

export default UserHistory;
